import { Component, Input, OnDestroy, OnInit, computed, effect, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { GS1HeaderService, HeaderAlert, GS1LayoutModule, GS1ButtonDirective } from '@gs1france/gs1-components-library';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { MaintainService } from '@services/maintain.service';
import { MaintenanceMessageService } from '@services/maintenanceMessage.service';
import { StorageService } from '@services/storage.service';
import { TokenGS1Service } from '@services/tokenGS1.service';
import { OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { Subscription } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'header-comp',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],

  imports: [RouterLink, RouterLinkActive, TranslateModule, GS1LayoutModule, GS1ButtonDirective],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() pageTitle: string;
  isAuthorizedSubscription: Subscription;
  isLoggedIn = false;
  avatarDivOver = false;
  logoffDivOver = false;
  isDropdownSelectAccountVisible = false;

  // Inject services first
  private router = inject(Router);
  private infosMessageService = inject(MaintainService);
  private oauthService = inject(OAuthService);
  private storageService = inject(StorageService);
  private translate = inject(TranslateService);
  private _header = inject(GS1HeaderService);
  private _tokenGS1Service = inject(TokenGS1Service);
  private maintenanceMessageService = inject(MaintenanceMessageService);

  // Signals from observables
  isFederation = toSignal(this.storageService.isUserFederation(), {
    initialValue: false,
  });
  companyDisplay = toSignal(this.storageService.getCompanyDisplayedSections(), {
    initialValue: null,
  });
  settings = toSignal(this.storageService.getSettings(), {
    initialValue: null,
  });
  tokenAdmin = toSignal(this._tokenGS1Service.getTokenGS1Admin(), {
    initialValue: '',
  });
  maintenanceMessage = toSignal(this.maintenanceMessageService.getMaintenanceMessage(), { initialValue: null });

  // Computed signals
  hasCustomerNumber = computed(() => this.companyDisplay()?.hasCustomerNumber ?? false);
  disablePackSetting = computed(() => {
    const settingsValue = this.settings();
    const disablePack = settingsValue?.values.DisablePackPartenaire;
    return disablePack?.defaultValue?.toLowerCase() === 'true';
  });

  multiple = false;
  session: any;
  nomEntreprise = '';
  nomComplet: string;
  emailAdherent = '';
  listeLangues: string[] = ['fr', 'en'];
  langueChosen = '';
  informationsMessageMaintain: string;
  displayBanner = false;
  actifBanner = false;
  subs: Subscription[] = [];

  constructor() {
    this.isLoggedIn = this.oauthService.hasValidAccessToken();
    this.isAuthorizedSubscription = this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthSuccessEvent && (event.type === 'token_received' || event.type === 'token_refreshed')) {
        if (this.oauthService.hasValidAccessToken()) {
          this.isLoggedIn = true;
        }
      }
    });

    // Effect to handle token admin storage
    effect(() => {
      const token = this.tokenAdmin();
      if (token) {
        localStorage.setItem('tokenAdmin', token);
      }
    });

    // Effect to handle maintenance message notification
    effect(() => {
      const message = this.maintenanceMessage();
      if (message) {
        this._header.addAlert(new HeaderAlert(message.theme, message.message));
      }
    });
  }

  ngOnInit() {
    this.session = this.storageService.getSession();
    this.multiple = this.storageService.getMultiple();

    if (this.session) {
      this.nomEntreprise = this.session['company_name'];
      this.emailAdherent = this.session['email'];
      this.nomComplet = this.storageService.getNomComplet();
    }
    this.subs.push(this.storageService.nomComplet.subscribe((value) => (this.nomComplet = value)));
    this.subs.push(
      this.storageService.session.subscribe((value) => {
        this.session = value;
        this.nomEntreprise = this.session['company_name'];
        this.emailAdherent = this.session['email'];
        this.nomComplet = this.storageService.getNomComplet();
      })
    );
    this.subs.push(this.storageService.multiple.subscribe((value) => (this.multiple = value)));

    if (!this.browserIsCompatible()) {
      setTimeout(() => {
        this._header.addAlert(
          new HeaderAlert(
            'warning',
            "Veuillez changer ou mettre à jour votre navigateur internet pour bénéficier d'une expérience de visionnage optimale."
          )
        );
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    this.isAuthorizedSubscription.unsubscribe();
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  logout() {
    this.storageService.clearStorage();
    this.oauthService.logOut();
  }

  enterAvatarDiv() {
    this.avatarDivOver = true;
  }
  leaveAvatarDiv() {
    setTimeout(() => {
      this.avatarDivOver = false;
    }, 200);
  }
  enterUserInfosDiv() {
    this.logoffDivOver = true;
  }
  leaveUserInfosDiv() {
    setTimeout(() => {
      this.logoffDivOver = false;
    }, 200);
  }

  changeAccount() {
    if (this.isDropdownSelectAccountVisible) {
      this.isDropdownSelectAccountVisible = false;
    } else {
      this.isDropdownSelectAccountVisible = true;
    }
  }

  changeCompany() {
    this.storageService.removeCompanyData();
    try {
      this.oauthService.customQueryParams = {
        companies: true,
        prompt: 'login',
      };
      this.oauthService.resetImplicitFlow();
      this.oauthService.initImplicitFlow();
    } catch {}
  }

  showGreyIcon() {
    const isHub = window.location.pathname == '/hub';
    if (isHub) return true;
    else return false;
  }

  public onChange(event: { target: { value: string } }): void {
    this.langueChosen = event.target.value;
    this.translate.use(this.langueChosen);
  }

  browserIsCompatible() {
    if (typeof ResizeObserver === 'undefined') {
      return false;
    }
    return true;
  }
}
